/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import queryString from 'querystring';

import { Endpoints, Infer } from 'shared';

import { store } from '../store';
import { authRefreshUser } from '../store/auth/actions';
import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { CreateProjectRequestBody } from '../types/project/CreateProjectRequestBody';
import { UpdateProjectRequestBody } from '../types/project/UpdateProjectRequestBody';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class ProjectApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchProjects = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetProjects>['Response'][200]>(
      `/organization/${organizationId}/project/`,
    );
  };

  fetchProjectsWithClusters = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetProjectsWithClusters>['Response'][200]>(
      `/organization/${organizationId}/project/cluster`,
    );
  };

  updateProject = ({
    projectId,
    organizationId,
    data,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    data: UpdateProjectRequestBody;
  }) => {
    return this.api.patch<Infer<typeof Endpoints.PatchProject>['Response'][200]>(
      `/organization/${organizationId}/project/${projectId}`,
      data,
    );
  };

  updateProjectThumbnail = ({
    projectId,
    organizationId,
    body,
  }: {
    organizationId: EntityId;
    projectId: EntityId;
    body: { seed: string } | { base64: string };
  }) => {
    const formattedBody =
      'base64' in body ? { type: 'base64', base64: body.base64 } : { type: 'seed', seed: body.seed };
    return this.api.put(`/organization/${organizationId}/project/${projectId}/thumbnail`, formattedBody);
  };

  fetchProject = (organizationId: EntityId, projectId: EntityId) => {
    return this.api.get<Infer<typeof Endpoints.GetProject>['Response'][200]>(
      Endpoints.GetProject.route({ organizationId, projectId }),
    );
  };

  getProjectThumbnailUrl = ({
    projectId,
    organizationId,
    v = 1,
  }: {
    projectId: EntityId;
    organizationId: EntityId;
    v?: string | number;
  }) => {
    return `${getApiEndpoint()}/organization/${organizationId}/project/${projectId}/thumbnail?${queryString.encode({
      v,
    })}`;
  };

  deleteProject = ({ organizationId, projectId }: { organizationId: EntityId; projectId: EntityId }) => {
    return this.api.del(`/organization/${organizationId}/project/${projectId}`);
  };

  createProject = ({ organizationId, data }: { organizationId: EntityId; data: CreateProjectRequestBody }) => {
    return this.api
      .post<Infer<typeof Endpoints.PostProject>['Response'][200]>(Endpoints.PostProject.route({ organizationId }), data)
      .then(data => {
        store.dispatch(authRefreshUser());
        return data;
      });
  };

  deleteProjectThumbnail = ({ organizationId, projectId }: { organizationId: EntityId; projectId: EntityId }) => {
    return this.api.del(`/organization/${organizationId}/project/${projectId}/thumbnail`);
  };
}

export const ProjectApiService = new ProjectApiServiceClass();
